import React, {useEffect, useState} from "react";
import {
    Button,
    Card,
    message,
    Popover,
    Row,
    Table,
    Tooltip,
    Checkbox,
    Col,
    Space,
    Modal,
    Popconfirm
} from "antd";
import {Link} from "react-router-dom";
import JSONInput from "react-json-editor-ajrm";

import {b64DecodeToUrl, formatMomentDate} from "../../../../../utils/helpers";
import {useFetch} from "../../../../../hooks/useFetch";
import {useStore} from "../../../../../hooks/useStore";
import InactiveCourierWalletsTabFilters from "./InactiveCourierWalletsTabFilters";
import WithdrawSummaryModal from "./WithdrawSummaryModal";
import {CourierInvoicesExpandedRow} from "./CourierInvoicesExpandedRow";
import moment from "moment";
import {
    InfoCircleOutlined,
    DownloadOutlined,
    MoneyCollectOutlined,
    SendOutlined
} from "@ant-design/icons";
import {formatMoney} from "../../../../../utils/moneyHelper";

const PAGE_SIZE = 15;

export const InactiveCourierWalletsTab = ({isTabOpened}) => {
    const stores = useStore();
    const canExport = stores.meStore.checkPermission("walletInactiveCouriers.export");
    const [filters, setFilters] = useState({page: 1, city: "all", isCourierActive: false});
    const [hideEmptyWallets, setHideEmptyWallets] = useState(false);
    const [loadingExcelData, setLoadingExcelData] = useState(false);
    const [loadingCourierExcel, setLoadingCourierExcel] = useState({});
    const [withdrawModalVisible, setWithdrawModalVisible] = useState(false);
    const [withdrawSummaryLoading, setWithdrawSummaryLoading] = useState(false);
    const [withdrawSummaryData, setWithdrawSummaryData] = useState(null);
    const [selectedCourier, setSelectedCourier] = useState(null);
    const [withdrawConfirmLoading, setWithdrawConfirmLoading] = useState(false);
    const [bulkWithdrawConfirmVisible, setBulkWithdrawConfirmVisible] = useState(false);
    const [terminatedCouriersCount, setTerminatedCouriersCount] = useState(null);
    const [bulkWithdrawLoading, , , , triggerBulkWithdraw] = useFetch({
        fetchFn: () => stores.walletStore.bulkWithdrawTerminated(),
        condition: false,
        initialCall: false,
        waitForResponse: true
    });
    const [terminatedCouriersCountLoading, , , , fetchTerminatedCouriersCount] = useFetch({
        fetchFn: () => stores.walletStore.getTerminatedCouriersCountForWithdraw(),
        condition: false,
        initialCall: false,
        waitForResponse: true,
        defaultDataValue: null,
        transform: response => response?.data?.count ?? response?.data ?? null,
        callbackFunc: response => showBulkWithdrawConfirm(response?.data?.count)
    });
    const hideForOperationRole = stores.meStore.checkRole("hideForOperationRole");
    const hideForEquipmentManagerRole = stores.meStore.checkRole("hideForEquipmentManagerRole");
    const isAgency = stores.meStore.checkRole("isAgency");
    const showActionButtons = !hideForOperationRole && !hideForEquipmentManagerRole && !isAgency;
    const [loading, terminals, , , refetchWalletCouriers] = useFetch({
        fetchFn: stores.walletStore.listWalletCouriers.bind(null, {
            ...filters,
            hideEmptyWallets,
            skip: (filters.page - 1) * PAGE_SIZE,
            limit: PAGE_SIZE,
            excel: false
        }),
        dependencies: [filters, hideEmptyWallets, isTabOpened],
        waitForResponse: true,
        condition: isTabOpened
    });

    const columns = [
        {
            title: "Kurye",
            dataIndex: ["courier", "fullname"],
            width: 180,
            key: "courier",
            render: (fullname, row) =>
                row?.courier ? (
                    stores.meStore.checkRole("notVisibleForCompanyUser") ? (
                        <Link
                            target="_blank"
                            to={{
                                pathname: "/couriers/" + row?.courier?._id
                            }}
                        >
                            {row?.courier?.fullname}
                        </Link>
                    ) : (
                        row?.courier?.fullname ?? "Kurye"
                    )
                ) : (
                    "-"
                )
        },
        {
            title: "TCKN",
            dataIndex: ["courier", "tckn"],
            key: "tckn",
            width: 150,
            render: tckn =>
                stores.meStore.checkRole("notVisibleForCompanyUser") ? <>{tckn}</> : <>-</>
        },
        {
            title: "Telefon",
            dataIndex: ["courier", "cellPhone"],
            key: "cellPhone",
            width: 150,
            render: cellPhone =>
                stores.meStore.checkRole("notVisibleForCompanyUser") ? <>{cellPhone}</> : <>-</>
        },
        {
            title: "Şehir",
            dataIndex: ["courier", "city"],
            key: "city",
            width: 100,
            render: city => <>{city?.capitalize() || city}</>
        },
        {
            title: "Bakiye",
            dataIndex: "balance",
            key: "balance",
            width: 150,
            render: (balance, row) => (
                <>
                    {formatMoney({amount: balance})}

                    <Tooltip
                        title={
                            <div style={{width: "200px"}}>
                                <div style={{display: "flex", justifyContent: "space-between"}}>
                                    <b>Bakiye : </b>
                                    {formatMoney({amount: balance})}
                                </div>
                                <div style={{display: "flex", justifyContent: "space-between"}}>
                                    <b>Bloke Bakiye : </b>
                                    {formatMoney({amount: row?.blockedBalance})}
                                </div>
                            </div>
                        }
                    >
                        <InfoCircleOutlined style={{marginLeft: 4, color: "#797979"}} />
                    </Tooltip>
                </>
            )
        },
        {
            title: "Mevcut Çekim Talebi Tutarı",
            dataIndex: "activeWithdrawRequestAmount",
            key: "activeWithdrawRequestAmount",
            width: 170,
            render: amount => formatMoney({amount})
        },
        {
            title: "Kesilmesi Beklenen Fatura Tutarı",
            dataIndex: "expectedInvoiceAmount",
            key: "expectedInvoiceAmount",
            width: 170,
            render: amount => formatMoney({amount})
        },
        {
            title: "Onay Bekleyen Fatura Tutarı",
            dataIndex: "pendingInvoiceAmount",
            key: "pendingInvoiceAmount",
            width: 170,
            render: amount => formatMoney({amount})
        },
        {
            title: "Entegratör",
            width: 90,
            dataIndex: ["courierInvoiceAccount", "vendor"],
            key: "vendor",
            render: (vendor, row) => {
                const courierVendor = vendor || row?.courier?.vendor;
                return courierVendor ? courierVendor : "-";
            }
        },
        {
            title: <>Entegratör Durumu</>,
            width: 160,
            dataIndex: ["courierInvoiceAccount", "status"],
            key: "status",
            render: (status, row) => (
                <>
                    {status ? status : "-"}
                    {row?.courierInvoiceAccount?.metaData && (
                        <Popover
                            content={
                                <JSONInput
                                    id="courierInvoiceAccountJSON"
                                    placeholder={row?.courierInvoiceAccount?.metaData}
                                    theme="dark_mitsuketa_tribute"
                                    width="auto"
                                    height="auto"
                                    viewOnly={true}
                                    confirmGood={false}
                                />
                            }
                        >
                            <InfoCircleOutlined style={{marginLeft: 4, color: "#797979"}} />
                        </Popover>
                    )}
                </>
            )
        },
        {
            title: <>Cüzdan Oluşturulma Tarihi</>,
            dataIndex: "createdAt",
            key: "createdAt",
            width: 200,
            ellipsis: {
                showTitle: false
            },
            render: createdAt => (
                <Tooltip title={formatMomentDate(createdAt)}>
                    {moment(createdAt).format("lll")}
                </Tooltip>
            )
        },
        {
            title: "Cüzdan Durumu",
            width: 150,
            dataIndex: ["courier", "isWalletActive"],
            key: "isWalletActive",
            align: "center",
            render: isWalletActive => (isWalletActive ? "Aktif" : "Pasif")
        },
        ...(hideForEquipmentManagerRole || !canExport
            ? []
            : [
                  {
                      title: "İşlemler",
                      width: 150,
                      key: "actions",
                      align: "center",
                      render: (_, row) => (
                          <Space size="small">
                              {showActionButtons && (
                                  <Tooltip title="Tekrar SMS Gönder">
                                      <Button
                                          type="primary"
                                          size="small"
                                          icon={<SendOutlined />}
                                          onClick={() =>
                                              stores.walletStore.sendWithdrawSms({
                                                  courierId: row?.courier?._id
                                              })
                                          }
                                      />
                                  </Tooltip>
                              )}
                              {showActionButtons && canExport && (
                                  <Tooltip title="Kesinti excel'i oluştur">
                                      <Button
                                          type="primary"
                                          size="small"
                                          icon={<DownloadOutlined />}
                                          loading={loadingCourierExcel[row?.courier?._id]}
                                          onClick={() =>
                                              getCourierFinancialExcel(
                                                  row?.courier?._id,
                                                  row?.courier?.fullname
                                              )
                                          }
                                          disabled={!row?.courier?._id}
                                      />
                                  </Tooltip>
                              )}
                              {showActionButtons && row?.expectedInvoiceAmount > 0 && (
                                  <Tooltip title="Çekim Talebi Oluştur">
                                      <Button
                                          type="default"
                                          size="small"
                                          icon={<MoneyCollectOutlined />}
                                          onClick={() =>
                                              handleWithdrawClick(
                                                  row?.courier?._id,
                                                  row?.courier?.fullname,
                                                  row?.balance
                                              )
                                          }
                                          disabled={!row?.courier?._id}
                                      />
                                  </Tooltip>
                              )}
                          </Space>
                      )
                  }
              ])
    ];

    const getExcelExport = async () => {
        const messageKey = "inactive-wallet-couriers-excel";
        message.loading({content: "Excel hazırlanıyor...", key: messageKey});
        setLoadingExcelData(true);
        try {
            const response = await stores.walletStore.listWalletCouriers({
                ...filters,
                hideEmptyWallets,
                excel: true
            });

            const base64Data = response?.data;
            const blobUrl = await b64DecodeToUrl(base64Data);
            const link = document.createElement("a");
            link.href = blobUrl;
            link.setAttribute("download", "inaktif_kurye_cuzdan_listesi.xlsx");
            document.body.appendChild(link);
            link.click();
            link.remove();
            message.success({
                content: "Excel dosyası başarıyla indirildi.",
                key: messageKey,
                duration: 4
            });
        } catch (error) {
            console.log("error", error);
            message.error({
                content: "Excel dosyası indirilirken bir hata oluştu.",
                key: messageKey,
                duration: 4
            });
        } finally {
            setLoadingExcelData(false);
        }
    };

    const getCourierFinancialExcel = async (courierId, courierName) => {
        const messageKey = `courier-financial-excel-${courierId}`;
        message.loading({content: "Kesinti excel'i hazırlanıyor...", key: messageKey});
        setLoadingCourierExcel(prev => ({...prev, [courierId]: true}));
        try {
            const response = await stores.walletStore.createFinancialUploadExcelToSpecificCourier({
                courierId
            });

            if (response?.success && response?.data) {
                const base64Data = response.data.data;
                const filename = response.data.fileName;
                const blobUrl = await b64DecodeToUrl(base64Data);
                const link = document.createElement("a");
                link.href = blobUrl;
                link.setAttribute("download", filename || `${courierName}_kesinti_excel.xlsx`);
                document.body.appendChild(link);
                link.click();
                link.remove();
                message.success({
                    content: "Kesinti excel'i başarıyla indirildi.",
                    key: messageKey,
                    duration: 4
                });
            } else {
                message.error({
                    content: "Kesinti excel'i oluşturulamadı.",
                    key: messageKey,
                    duration: 4
                });
            }
        } catch (error) {
            console.log("error", error);
            message.error({
                content: "Kesinti excel'i oluşturulurken bir hata oluştu.",
                key: messageKey,
                duration: 4
            });
        } finally {
            setLoadingCourierExcel(prev => ({...prev, [courierId]: false}));
        }
    };

    const handleWithdrawClick = async (courierId, courierName, balance) => {
        setSelectedCourier({id: courierId, name: courierName, balance: balance});
        setWithdrawModalVisible(true);
        setWithdrawSummaryLoading(true);
        setWithdrawSummaryData(null);

        try {
            const response = await stores.walletStore.generateWithdrawSummary({
                courierId,
                amount: balance
            });

            if (response?.success) {
                if (response.data?.success !== false) {
                    setWithdrawSummaryData(response.data);
                } else {
                    message.error(response.data?.message || "Para yatırma özeti oluşturulamadı.");
                    setWithdrawModalVisible(false);
                }
            } else {
                message.error(response?.message || "Para yatırma özeti oluşturulamadı.");
                setWithdrawModalVisible(false);
            }
        } catch (error) {
            console.log("error", error);
            message.error("Para yatırma özeti oluşturulurken bir hata oluştu.");
            setWithdrawModalVisible(false);
        } finally {
            setWithdrawSummaryLoading(false);
        }
    };

    const handleWithdrawConfirm = () => {
        Modal.confirm({
            title: "Çekim Talebi Onayı",
            content: (
                <div>
                    <p>
                        <strong>{selectedCourier?.name}</strong> adlı kurye için çekim talebi
                        oluşturmayı onaylıyor musunuz?
                    </p>
                    <p style={{color: "#ff4d4f", marginTop: 12}}>
                        ⚠️ Son kesintileri eklediğinize emin misiniz?
                    </p>
                    <p style={{color: "#666", fontSize: "12px", marginTop: 8}}>
                        Bu işlem geri alınamaz. Onayladıktan sonra ödeme kurye hesabına
                        yatırılacaktır.
                    </p>
                </div>
            ),
            okText: "Evet, Onaylıyorum",
            cancelText: "İptal Et",
            okType: "primary",
            icon: null,
            width: 500,
            onOk: async () => {
                await performWithdrawApproval();
            }
        });
    };

    const performWithdrawApproval = async () => {
        if (!selectedCourier?.id || !selectedCourier?.balance) return;

        setWithdrawConfirmLoading(true);
        try {
            const response = await stores.walletStore.approveWithdraw({
                courierId: selectedCourier.id,
                amount: selectedCourier.balance
            });

            if (response?.success) {
                message.success(response.message || "Para yatırma işlemi başarıyla onaylandı!");
                setWithdrawModalVisible(false);
                setSelectedCourier(null);
                setWithdrawSummaryData(null);
                refetchWalletCouriers();
            } else {
                message.error("Para yatırma işlemi onaylanamadı.");
            }
        } catch (error) {
            console.log("error", error);
            message.error("Para yatırma işlemi onaylanırken bir hata oluştu.");
        } finally {
            setWithdrawConfirmLoading(false);
        }
    };

    const handleBulkWithdrawClick = async () => {
        await fetchTerminatedCouriersCount();
    };

    const showBulkWithdrawConfirm = count => {
        setTerminatedCouriersCount(count);
        setBulkWithdrawConfirmVisible(true);
    };

    const performBulkWithdraw = async () => {
        // Başarı ve hata mesajları walletStore içinde gösteriliyor.
        await triggerBulkWithdraw();
        setBulkWithdrawConfirmVisible(false);
        refetchWalletCouriers();
    };

    const handleWithdrawCancel = () => {
        setWithdrawModalVisible(false);
        setSelectedCourier(null);
        setWithdrawSummaryData(null);
    };

    return (
        <Card>
            <Row gutter={[16, 16]} style={{marginBottom: 8}}>
                <InactiveCourierWalletsTabFilters filters={filters} setFilters={setFilters} />
                <Col xs={24} sm={12} md={6} lg={6} xl={6}>
                    <h5 style={{visibility: "hidden"}}>-</h5>
                    <div style={{minHeight: 32, display: "flex", alignItems: "center"}}>
                        <Checkbox
                            checked={hideEmptyWallets}
                            onChange={e => setHideEmptyWallets(e.target.checked)}
                        >
                            Bakiyesi 0 Olanları Gizle
                        </Checkbox>
                    </div>
                </Col>
            </Row>
            <Row gutter={[16, 16]} style={{marginBottom: 8, gap: "16px"}} justify="end">
                {canExport && (
                    <Button type="primary" onClick={getExcelExport} loading={loadingExcelData}>
                        {loadingExcelData ? "Excel Hazırlanıyor..." : "Excel İndir"}
                    </Button>
                )}
                {showActionButtons && (
                    <Popconfirm
                        title={
                            terminatedCouriersCount === 0
                                ? "Cüzdanda bakiye olan pasif kurye bulunmamaktadır."
                                : `Cüzdanında bakiyesi olan ${terminatedCouriersCount} pasif kurye için para çekim talebi oluşturuyorsunuz. Devam Edilsin mi?`
                        }
                        visible={bulkWithdrawConfirmVisible}
                        okText="Onayla"
                        cancelText="İptal Et"
                        okButtonProps={{
                            disabled: terminatedCouriersCount === 0,
                            loading: bulkWithdrawLoading
                        }}
                        icon={<InfoCircleOutlined style={{color: "#1890ff"}} />}
                        overlayStyle={
                            terminatedCouriersCount === 0 ? {whiteSpace: "nowrap"} : undefined
                        }
                        onConfirm={performBulkWithdraw}
                        onCancel={() => setBulkWithdrawConfirmVisible(false)}
                    >
                        <Button
                            type="primary"
                            onClick={handleBulkWithdrawClick}
                            loading={bulkWithdrawLoading || terminatedCouriersCountLoading}
                        >
                            Toplu Fatura/Para Çekim Talebi Oluştur
                        </Button>
                    </Popconfirm>
                )}
            </Row>
            <Table
                loading={loading}
                columns={columns}
                size="small"
                dataSource={
                    terminals?.rows?.map(row => ({
                        ...row,
                        rowKey: `${row?._id}-${row?.courier?._id}-${row?.courierInvoiceAccount?._id}`
                    })) || []
                }
                rowKey={"rowKey"}
                tableLayout="fixed"
                scroll={{x: 1860}}
                // BE: isCourierActive false iken fatura bekleyen courierInvoice kayıtları row.courierInvoices altında dönüyor
                expandable={{
                    expandedRowRender: row => (
                        <CourierInvoicesExpandedRow
                            courierId={row?.courier?._id}
                            invoices={row?.courierInvoices || []}
                        />
                    ),
                    rowExpandable: row => (row?.courierInvoices?.length ?? 0) > 0
                }}
                pagination={{
                    total: terminals?.dataCount || 0,
                    current: filters.page,
                    hideOnSinglePage: true,
                    showSizeChanger: false,
                    pageSize: PAGE_SIZE,
                    onChange: page => {
                        setFilters({...filters, page});
                    }
                }}
            />
            <WithdrawSummaryModal
                visible={withdrawModalVisible}
                onCancel={handleWithdrawCancel}
                onConfirm={handleWithdrawConfirm}
                loading={withdrawSummaryLoading}
                confirmLoading={withdrawConfirmLoading}
                summaryData={withdrawSummaryData}
                courierName={selectedCourier?.name}
            />
        </Card>
    );
};
